import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LangContext } from '../contexts/langContext';
import { StateContext } from '../contexts/stateContext';
import { yoni_dictionary, nakshatra_yoni_dictionary } from '../static/jyotish_local';
import { getJyotish } from './Edu/utilsJyotish';
import { CityDateTime } from './UiKit/CityDateTime';
import { _getCity } from './utilsGeo';
import './UiKit/Jyotish/yoni.css';
import Social from './Social';
import { PublicSocial } from './PublicSocial';
import { SocialContext } from '../contexts/socialContext';
import {
  AVATAR_TOKEN_LENGTH,
  USER_TOKEN_LENGTH,
  _getPublicUser,
  _getNewDesign,
  parseAvatar,
  applyDesignTheme,
  applyYoniBlackBackground,
} from './utilsDesign';
import { getLastDesignForTheme } from './theme';
import { Topbar } from './UiKit/Topbar';
import { Pop } from './UiKit/Pop';
import { MeInfo } from './SocialComponents/MeInfo';
import { PersonaInfo } from './SocialComponents/PersonaInfo';
import { UserInfo } from './SocialComponents/UseerInfo';
import { UserNetwork } from './SocialComponents/UserNetwork';
import { AvatarDob, AvatarPanel, PublicDob } from './UiKit/AvatarPanel';
import { Jyotish, JyotishMini } from './Edu/Jyotish';
import { Study } from './Edu/Study';
import { WithVersion } from './UiKit/VersionToggler';
import { PreloaderHD } from './UiKit/PreloaderHD';
import { MobMenu } from './UiKit/MobMenu';
import { YoniBackground } from './UiKit/YoniBackground';
import { Logo } from './UiKit/Logo';
import { YoniLogo } from './UiKit/Jyotish/YoniLogo';
import { DropDownDivider } from './UiKit/DropDown';
import { Button } from './UiKit/Button';
import { Persona } from './Persona';
import { Preview } from './UiKit/Preview';
import { Hr } from './UiKit/Hr';
import { Txt } from './UiKit/Txt';



const ANIMATION_DURATION_MS = 3000;
const NAKSHATRA_COUNT = 27;
const NUM_ROTATIONS = 1;
const TARGET_FINAL_PX = 192;
const BASE_TEXT_PX = 18;
const ZOOM_MAX = TARGET_FINAL_PX / 28;

const easeOutQuad = (t) => 1 - (1 - t) * (1 - t);
const easeInCubic = (t) => t * t * t;
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const clamp01 = (v) => Math.max(0, Math.min(1, v));


const getYoniByNakshatra = (nakshatraName) => {
  if (!nakshatraName || typeof nakshatraName !== 'string') return null;
  const normalizedName = nakshatraName.toLowerCase().replace(/\s+/g, '_');
  const yoniInfo = nakshatra_yoni_dictionary[normalizedName];
  if (!yoniInfo) return null;
  return {
    emoji: yoniInfo.yoniEmoji || '–',
    title: yoniInfo.yoniTitle || '–',
    id: yoniInfo.yoniId || null,
    order: yoniInfo.order || null,
  };
};


function YoniCircle({ progress, targetOrder, language = 'en', idle = false }) {
  const [size, setSize] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 800,
    h: typeof window !== 'undefined' ? window.innerHeight : 600,
  }));

  useEffect(() => {
    const onResize = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const items = useMemo(
    () =>
      Object.values(nakshatra_yoni_dictionary)
        .filter((n) => typeof n.order === 'number')
        .sort((a, b) => a.order - b.order),
    [],
  );

  const getYoniDictEntry = (yoniId) => {
    if (!yoniId) return null;
    // yoni_dictionary keys are mostly aligned with yoniId, but for "vanara" the
    // entry lives under key "ram" (id: "vanara") — fall back to a scan by id.
    return (
      yoni_dictionary[yoniId] ||
      Object.values(yoni_dictionary).find((y) => y.id === yoniId) ||
      null
    );
  };

  const getLocalizedSanskrit = (yoniId) => {
    const entry = getYoniDictEntry(yoniId);
    return entry?.[`title_sanskrit-${language}`] || entry?.['title_sanskrit-en'] || '';
  };

  const getYoniDevanagari = (yoniId) => getYoniDictEntry(yoniId)?.sanskrit || '';

  // Big circle so 27 Sanskrit labels don't overlap; allowed to overflow narrow viewports.
  const baseRadius = Math.max(320, Math.min(size.w, size.h) * 0.45);

  // Single eased parameter drives both zoom and camera pan, so the top of the
  // circle (where the target lands) travels monotonically from -baseRadius to 0
  // without overshooting upward mid-animation.
  const eased = easeInOutCubic(progress);
  const zoom = 1 + (ZOOM_MAX - 1) * eased;
  const rotationRad = easeOutQuad(progress) * Math.PI * 2 * NUM_ROTATIONS;
  // Solve (1 - cameraT) * zoom = (1 - eased) so screenY of the top point = -baseRadius * (1 - eased).
  const cameraT = progress >= 1 ? 1 : 1 - (1 - eased) / zoom;

  const cameraOffsetX = 0;
  const cameraOffsetY = -baseRadius * cameraT;

  const otherOpacity = clamp01(1 - easeInCubic(progress) * 1.3);
  const textSize = BASE_TEXT_PX * zoom;

  return (
    <div className={`yoni_circle ${idle ? 'yoni_circle--idle' : ''}`}>
      {items.map((item) => {
        // Offset by targetOrder so target starts at angle -π/2 (top of circle).
        const angle =
          (item.order - targetOrder) * ((2 * Math.PI) / NAKSHATRA_COUNT) - Math.PI / 2 + rotationRad;
        const worldX = baseRadius * Math.cos(angle);
        const worldY = baseRadius * Math.sin(angle);
        const screenX = (worldX - cameraOffsetX) * zoom;
        const screenY = (worldY - cameraOffsetY) * zoom;
        const isTarget = item.order === targetOrder;
        const tangentDeg = ((angle + Math.PI / 2) * 180) / Math.PI;
        return (
          <div
            key={item.id}
            className="yoni_circle_item"
            style={{
              transform: `translate(${screenX}px, ${screenY}px) rotate(${tangentDeg}deg)`,
              fontSize: `${textSize}px`,
              fontWeight: isTarget ? 700 : 500,
              opacity: isTarget ? 1 : otherOpacity,
              zIndex: isTarget ? 2 : 1,
            }}
          >
            {getYoniDevanagari(item.yoniId) || item.sanskrit || item.title}
            <div className="yoni_page_item_eng">
              {getLocalizedSanskrit(item.yoniId)}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// Slowly cross-fading yoni images on the landing background. One <img> at a
// time fades in, holds, then fades out; on each cycle we advance to the next
// picture, so the whole pantheon drifts past behind the logo/CTA.
const YONI_IMG_CYCLE_MS = 2000;

function YoniImageBackground() {
  const images = useMemo(
    () =>
      Object.values(yoni_dictionary)
        .map((y) => y.image)
        .filter(Boolean)
        .map((src) => `${process.env.PUBLIC_URL || ''}${src}`),
    [],
  );

  const [index, setIndex] = useState(() =>
    images.length ? Math.floor(Math.random() * images.length) : 0,
  );

  useEffect(() => {
    if (images.length < 2) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, YONI_IMG_CYCLE_MS);
    return () => clearInterval(id);
  }, [images.length]);

  if (images.length === 0) return null;

  return (
    <div className="yoni_idle_bg" aria-hidden="true">
      {/* key forces a remount each cycle so the fade keyframe restarts. */}
      <img key={index} src={images[index]} alt="" className="yoni_idle_bg_img" />
    </div>
  );
}


function YoniLayout({ viewedUser, viewedAvatar, viewedFollowings, viewedFollowers, surroundingsHidden = false, children }) {
  const socialContext = useContext(SocialContext);
  const stateContext = useContext(StateContext);
  const langContext = useContext(LangContext);
  const $ui = langContext.$ui;
  const me = socialContext?.me;
  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { useravatarId } = useParams();

  // /yoni/:useravatarId/info | /followers | /followings — same pops as /u/:id/*.
  const isInfo = location.pathname.includes('/info');
  const isFollowersPage = location.pathname.includes('/followers');
  const isFollowingsPage = location.pathname.includes('/followings');
  // Routes are flat (not nested), so relative `navigate('../')` would resolve
  // to the route parent `/` — close explicitly back to /yoni/:useravatarId.
  const closePop = () => navigate(`/yoni/${useravatarId}`);


  // Topbar shows the logged-in user (or the viewed avatar for guests).
  const topbarUser = me?.user || viewedUser || null;
  const topbarAvatar = me?.avatar || viewedAvatar || null;

  // AvatarPanel describes the avatar/user on the page (the one we're calculating
  // yoni for) — falls back to the logged-in user when navigating without a token.
  const panelUser = viewedUser || me?.user || null;
  const panelAvatar = viewedAvatar || me?.avatar || null;
  const isMe = Boolean(
    me?.avatar?.token && panelAvatar?.token && me.avatar.token === panelAvatar.token,
  );

  // Same navigation pattern as Edu/Full.js — drop into /study/<what> while
  // preserving any existing query string.


  return (
    <>
      <YoniBackground />
      <div className="yoni_page_container">

        {Boolean(socialContext.me) && <MobMenu 
          avatar={panelAvatar} 
          mode='yoni'
          hidden={surroundingsHidden}
          showItems={[
            'people',
            'history',
            'bodygraphs',
            'pentas',
          ]}
        />}

        {topbarAvatar ? (
          <Topbar
            user={topbarUser}
            avatar={topbarAvatar}
            mode2={scrolled}
            mode="yoni"
            logoColor="#ff7700"
            logoBgColor="#000"
            logoWaveColor="#ff7700"
            logoBpm={20}
          />
        ) : (
          <Topbar
            user={false}
            mode="yoni"
            logoColor="#ff7700"
            logoBgColor="#000"
            logoWaveColor="#ff7700"
            logoBpm={20}
          />
        )}

        {/* {Boolean(viewedAvatar) && (
          <div className={`yoni_surroundings ${surroundingsHidden ? 'yoni_surroundings--hidden' : ''}`}>
            <AvatarPanel
              mode="yoni"
              publ={!me}
              isMe={isMe}
              user={panelUser}
              avatar={panelAvatar}
              followings={viewedFollowings || []}
              followers={viewedFollowers || []}
            />
          </div>
        )} */}

        {isInfo
          && !viewedUser
          && Boolean(viewedAvatar)
          && <Pop
            handleClose={closePop}
          >
            <PersonaInfo
              avatar={viewedAvatar}
              handleClose={closePop}
            />
          </Pop>}

        {isInfo
          && viewedUser
          && viewedUser.isMe
          && <Pop
            handleClose={closePop}
          >
            <MeInfo
              user={me.user}
              title={$ui('pop_edit_me_header')}
              avatar={viewedAvatar}
              handleClose={closePop}
            />
          </Pop>}

        {isInfo
          && viewedUser
          && !viewedUser.isMe
          && <Pop
            handleClose={closePop}
          >
            <UserInfo
              title={$ui('pop_edit_avatar_header')}
              user={viewedUser}
              avatar={viewedAvatar}
              handleClose={closePop}
            />
          </Pop>}

        {(isFollowersPage || isFollowingsPage)
          && Boolean(me)
          && viewedUser
          && <Pop
            title={$ui('people_people')}
            icon='hub'
            handleClose={closePop}
            type={isFollowingsPage ? 'followings' : 'followers'}
          >
            <UserNetwork
              useravatarId={useravatarId}
              user={viewedUser}
              mode='yoni'
              startState={isFollowingsPage ? 'followings' : 'followers'}
            />
          </Pop>}

        {panelAvatar && (
          <Study
            mode='yoni'
            user={panelUser}
            avatar={panelAvatar}
          />
        )}

        <div className={`yoni_surroundings ${surroundingsHidden ? 'yoni_surroundings--hidden' : ''}`}>
          {Boolean(me) && <Social
            showItems={[
              'people',
              'history',
              'bodygraphs',
              'pentas',
            ]}
            mode='yoni'
          />}
          {/* {Boolean(me) ? <Social
            mode='yoni'
          /> : <PublicSocial
            mode='yoni'
          />} */}
        </div>
        {children}
      </div>
    </>
  );
}


export function Yoni() {
  const context = { ...useContext(LangContext) };
  const socialContext = useContext(SocialContext);
  const stateContext = useContext(StateContext);
  const tryDesignDate = stateContext?.state?.tryDesignDate || null;
  const $ui = context.$ui;
  const location = useLocation();
  const { useravatarId } = useParams();

  const urlParams = new URLSearchParams(window.location.search);
  const cityIdFromUrl = urlParams.get('city_id') ? parseInt(urlParams.get('city_id')) : 0;
  const dateParam = urlParams.get('date');
  const timeParam = urlParams.get('time');
  const timezoneParam = urlParams.get('timezone');
  const dobParam = urlParams.get('dob');
  const dobFromUrl =
    dobParam || (dateParam && timeParam ? `${dateParam}T${timeParam}:00` : null);
  const revealParamFromUrl = urlParams.get('reveal') === 'true';

  const [calcShown, setCalcShown] = useState(false);
  const [cityData, setCityData] = useState(null);
  const [kundli, setKundli] = useState(null);
  const [error, setError] = useState(null);
  const [avatarFromToken, setAvatarFromToken] = useState(null);
  // HD design computed from URL params (no token) — Preview/Bodygraph need a full
  // design object, which `avatarDobAvatar` doesn't carry on its own.
  const [designFromUrl, setDesignFromUrl] = useState(null);
  const [userFromToken, setUserFromToken] = useState(null);
  const [followingsFromToken, setFollowingsFromToken] = useState([]);
  const [followersFromToken, setFollowersFromToken] = useState([]);

  // All /yoni/ routes render this component, so tag <body> with `b--yoni`
  // while it's mounted to allow page-specific global styling.
  // Also lock black chrome — applyDesignTheme must not paint HD type colors here.
  useEffect(() => {
    document.body.classList.add('b--yoni');
    applyYoniBlackBackground();
    return () => {
      document.body.classList.remove('b--yoni');
      applyDesignTheme(getLastDesignForTheme());
    };
  }, []);

  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  // Set by the Skip button so an in-flight rAF step can't overwrite progress=1.
  const skipAnimationRef = useRef(false);
  // Animation should play only on initial fresh open — subsequent recomputes
  // (avatar switch via Social, or rectification slider) snap to the result.
  const hasAnimatedRef = useRef(false);
  // Background yoni image is held back for 500ms after the result lands so it
  // doesn't pop in simultaneously with the title/description.
  const [bgImageShown, setBgImageShown] = useState(false);
  // Background image scrolls up slower than the page (parallax). We drive a CSS
  // var on the <img> from a rAF-throttled scroll handler so React doesn't
  // re-render on every scroll frame.
  const bgImageRef = useRef(null);
  // The scrollable area is `.yoni_result0` (overflow-y:auto, flex:1), NOT the
  // window — so the parallax must read this container's scrollTop, otherwise
  // window.scrollY stays 0 and the (position:fixed) image never drifts.
  const resultScrollRef = useRef(null);
  useEffect(() => {
    let raf = null;
    const scroller = resultScrollRef.current;
    const getScroll = () =>
      scroller ? scroller.scrollTop : window.scrollY;
    const update = () => {
      raf = null;
      const el = bgImageRef.current;
      if (!el) return;
      const scroll = getScroll();
      // Normalised scroll progress over roughly one viewport of the scroller.
      const viewport =
        (scroller && scroller.clientHeight) || window.innerHeight || 1;
      const progress = Math.min(Math.max(scroll / viewport, 0), 1);
      // The image is position:fixed (pinned), so we drift it upward as the
      // content scrolls — at ~0.35x the scroll speed it reads as a slow parallax.
      el.style.setProperty('--yoni-parallax', `${-scroll * 0.35}px`);
      // The higher you scroll, the bigger and more transparent it gets.
      el.style.setProperty('--yoni-scale', `${1 + progress * 0.6}`);
      if (bgImageShown) {
        el.style.opacity = `${0.55 * (1 - progress * 0.85)}`;
      }
    };
    const onScroll = () => {
      if (raf === null) raf = requestAnimationFrame(update);
    };
    const target = scroller || window;
    target.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      target.removeEventListener('scroll', onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [bgImageShown]);
  // The result copy (title/description/nakshatra) appears AFTER the background
  // image has finished fading in, so the visual hierarchy is image → copy.
  const [copyShown, setCopyShown] = useState(false);
  // The HD block (CTA + bodygraph preview) only unfolds once ALL the appearance
  // animations (circle → image → copy) have fully finished.
  const [hdShown, setHdShown] = useState(false);
  // `?reveal=true` gates the reveal: result + animation are held until user
  // explicitly clicks the "Reveal my Instinct Archetype" button.
  const [revealPending, setRevealPending] = useState(revealParamFromUrl);
  // Set once the reveal button is pressed — turns the animation on for token URLs.
  const [revealTriggered, setRevealTriggered] = useState(false);

  // Resolve `/yoni/:useravatarId` — fetch avatar by user or avatar token.
  // When useravatarId disappears (e.g. user navigates back to bare `/yoni/`),
  // we also drop any avatar that was previously loaded from a token.
  const prevUseravatarIdRef = useRef(useravatarId);
  useEffect(() => {
    let cancelled = false;
    const useravatarIdChanged = prevUseravatarIdRef.current !== useravatarId;
    prevUseravatarIdRef.current = useravatarId;

    // Only wipe displayed data when the URL token actually changes. This effect
    // also re-runs when `socialContext.me` settles (localStorage cache → /api/me,
    // or login/logout) — resetting on every me update caused result → preloader →
    // result flicker on token URLs like /yoni/:useravatarId.
    if (useravatarIdChanged) {
      setAvatarFromToken(null);
      setUserFromToken(null);
      setFollowingsFromToken([]);
      setFollowersFromToken([]);

      if (!useravatarId) {
        // No token: city/kundli come from the URL params and are managed by their
        // own effects. Don't wipe cityData/kundli here — otherwise the city-fetch
        // effect (keyed on URL params) won't re-run and the page sticks on Loading.
        return () => {
          cancelled = true;
        };
      }

      // Token flow: clear stale derived data before fetching the new identity.
      setCityData(null);
      setKundli(null);
    } else if (!useravatarId) {
      return () => {
        cancelled = true;
      };
    }

    const applyAvatar = (ava) => {
      if (cancelled || !ava) return;
      setAvatarFromToken(parseAvatar(ava));
    };

    const handleUser = (newUser) => {
      if (cancelled || !newUser) return;
      if (newUser.user) setUserFromToken(newUser.user);
      if (newUser.followings) setFollowingsFromToken(newUser.followings);
      if (newUser.followers) setFollowersFromToken(newUser.followers);
      if (newUser.avatar) applyAvatar(newUser.avatar);
    };

    if (useravatarId.length === AVATAR_TOKEN_LENGTH) {
      // Avatar token — only resolvable for logged-in users (no public endpoint).
      if (socialContext?.x?.getAvatar) {
        socialContext.x.getAvatar(useravatarId).then(applyAvatar).catch((err) => {
          console.error('Yoni: getAvatar failed', err);
        });
      }
    } else if (useravatarId.length === USER_TOKEN_LENGTH) {
      if (socialContext?.me && socialContext?.x?.fetchUser) {
        socialContext.x.fetchUser(useravatarId, handleUser);
      } else {
        _getPublicUser(useravatarId, handleUser);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [useravatarId, socialContext?.me]);

  // On token-based URL we don't need (and should not show) the legacy share-link
  // params in the address bar — strip them and the `n=` hash flag.
  useEffect(() => {
    if (!useravatarId) return;
    const url = new URL(window.location.href);
    let changed = false;
    ['city', 'date', 'time', 'timezone', 'city_id', 'copy', 'dob'].forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });
    if (url.hash) {
      const hashParams = new URLSearchParams(url.hash.slice(1));
      if (hashParams.has('n')) {
        hashParams.delete('n');
        const newHash = hashParams.toString();
        url.hash = newHash ? `#${newHash}` : '';
        changed = true;
      }
    }
    if (changed) {
      window.history.replaceState(null, '', url.toString());
    }
  }, [useravatarId]);

  // Effective inputs: prefer avatar from token, fall back to URL params.
  const dob = avatarFromToken?.dob || dobFromUrl;
  const cityFromAvatar = avatarFromToken?.aggr?.city || null;
  const cityIdEffective = cityFromAvatar?.id || avatarFromToken?.city_id || cityIdFromUrl || 0;
  const hasInputs = Boolean(dob && (cityFromAvatar || cityIdEffective));

  // Minimal avatar for the public (URL-param) reveal screen. Memoized so the
  // reference is stable. We attach `aggr.city` from the already-fetched
  // cityData because AvatarDob reads the city only from `avatar.aggr.city`
  // (its own `_getCity` fallback is disabled) — without it the time line and the
  // "City, Country" line don't render.
  const avatarDobAvatar = useMemo(
    () =>
      dob
        ? {
          dob,
          city_id: cityIdEffective,
          aggr: cityData ? { city: cityData } : undefined,
          design: designFromUrl || undefined,
        }
        : null,
    [dob, cityIdEffective, cityData, designFromUrl],
  );

  // For URL-param (no token) reveals, fetch the full HD design from the design
  // service so the Bodygraph/Preview can render it (the token flow already
  // carries `avatar.design`).
  useEffect(() => {
    if (useravatarId) {
      setDesignFromUrl(null);
      return;
    }
    if (!dobFromUrl) {
      setDesignFromUrl(null);
      return;
    }
    const tz = timezoneParam || cityData?.timezone;
    if (!tz) return;
    let cancelled = false;
    _getNewDesign(dobFromUrl, tz, (newDesign) => {
      if (!cancelled) setDesignFromUrl(newDesign);
    });
    return () => {
      cancelled = true;
    };
  }, [useravatarId, dobFromUrl, timezoneParam, cityData?.timezone]);

  // Animation runs when:
  // - the page is opened via a "fresh" share link
  //   (?city=...&date=...&time=...&city_id=...), or
  // - the user just clicked the "Reveal my Instinct Archetype" button on a
  //   token-based URL with `?reveal=true`.
  // For other token-based URLs and for avatar switches inside the Social panel
  // we want the result instantly. While `revealPending` is true we hold the
  // animation off entirely.
  const shouldAnimate =
    !revealPending &&
    ((!useravatarId && Boolean(cityIdFromUrl && dobFromUrl)) || revealTriggered);


  const removeLanguageParam = () => {
    const hash = location.hash;
    const params = new URLSearchParams(hash.slice(1));
    params.delete('ln');
    const newHash = params.toString();
    window.history.replaceState(
      null,
      '',
      `${location.pathname}${location.search}${newHash ? `#${newHash}` : ''}`,
    );
  };

  useEffect(() => {
    const hash = location.hash;
    const params = new URLSearchParams(hash.slice(1));
    const languageParam = params.get('ln');
    if (
      languageParam &&
      languageParam !== context.language &&
      context.languages?.includes(languageParam)
    ) {
      context.setLanguage(languageParam);
    }
    removeLanguageParam();
  }, [location]);


  useEffect(() => {
    if (!hasInputs) return;
    // If the avatar already carries a usable city object, prefer it directly —
    // saves an extra round-trip to /api/geo/city/:id.
    if (cityFromAvatar?.location) {
      setCityData(cityFromAvatar);
      return;
    }
    // Always refetch when the effective city id changes — otherwise switching
    // avatars (whose new aggr.city has no `location`) would keep the old city.
    if (cityIdEffective) {
      _getCity(cityIdEffective, setCityData);
    }
  }, [cityFromAvatar, cityIdEffective, hasInputs]);


  // Treat tryDesignDate as a rectification override only when it actually
  // belongs to the current avatar. TimeRange in the Topbar pushes the slider
  // value into state.tryDesignDate continuously, so when you switch avatars
  // there's a window where the previous avatar's time is still in state — and
  // we don't want to apply that to a different person's birth chart.
  const rectifiedDob = useMemo(() => {
    if (!tryDesignDate) return null;
    const baseDob = avatarFromToken?.dob || dob;
    if (!baseDob) return null;
    const tryT = new Date(tryDesignDate).getTime();
    const baseT = new Date(baseDob).getTime();
    if (Number.isNaN(tryT) || Number.isNaN(baseT)) return null;
    // TimeRange's window is hoursPlus * 60 minutes; allow a generous 24h.
    return Math.abs(tryT - baseT) < 24 * 60 * 60 * 1000 ? tryDesignDate : null;
  }, [tryDesignDate, avatarFromToken?.dob, dob]);

  useEffect(() => {
    if (!hasInputs || !cityData?.location) return;
    try {
      setError(null);
      // Build the avatar object for getJyotish:
      // - prefer the full avatar from token (so design.meta.personality is honoured)
      // - if the rectification slider is active for THIS avatar, override both
      //   `dob` and `design.meta.personality` so the kundli reflects the rectified
      //   time live, just like other rectified pages.
      const baseAvatar = avatarFromToken
        ? { ...avatarFromToken, aggr: { ...avatarFromToken.aggr, city: cityData } }
        : { dob, aggr: { city: cityData } };

      // `rectifiedDob` is the TimeRange slider value — a naive LOCAL wall-clock
      // string on the same basis as `avatar.dob`. `design.meta.personality` is
      // ALREADY a UTC instant, so we must NOT re-derive it from local time.
      // Instead shift the existing UTC personality by the slider's delta from
      // the original birth time (the local time zone cancels out in the diff).
      // At the neutral slider position the delta is 0, so personality is left
      // exactly as stored — otherwise local time gets misread as UTC and the
      // Moon lands in the adjacent nakshatra (wrong yoni).
      let avatarForCalc = baseAvatar;
      if (rectifiedDob) {
        const baseDob = avatarFromToken?.dob || dob;
        const basePersonality = baseAvatar.design?.meta?.personality;
        const deltaMs = new Date(rectifiedDob).getTime() - new Date(baseDob).getTime();
        const basePersonalityMs = basePersonality
          ? new Date(`${String(basePersonality).replace(/Z$/, '')}Z`).getTime()
          : NaN;
        const rectifiedPersonality =
          Number.isFinite(basePersonalityMs) && Number.isFinite(deltaMs)
            ? new Date(basePersonalityMs + deltaMs).toISOString()
            : basePersonality;

        avatarForCalc = {
          ...baseAvatar,
          dob: rectifiedDob,
          design: {
            ...baseAvatar.design,
            meta: {
              ...(baseAvatar.design?.meta || {}),
              personality: rectifiedPersonality,
            },
          },
        };
      }

      const result = getJyotish(avatarForCalc);
      if (result?.kundli) {
        setKundli(result.kundli);
      }
    } catch (err) {
      console.error('Yoni jyotish calc failed:', err);
      setError(err.message || 'calc_failed');
    }
  }, [hasInputs, cityData, dob, avatarFromToken, rectifiedDob]);


  const language = context.language || 'en';
  const moonNakshatraName = kundli?.dasha?.birthNakshatra || null;
  const moonYoni = getYoniByNakshatra(moonNakshatraName);
  // yoni_dictionary keys are mostly aligned with yoniId, but for "vanara" the
  // entry lives under key "ram" (id: "vanara") — fall back to a scan by id.
  const yoniDetails = moonYoni?.id
    ? yoni_dictionary[moonYoni.id] ||
    Object.values(yoni_dictionary).find((y) => y.id === moonYoni.id) ||
    null
    : null;
  const yoniTitle =
    yoniDetails?.[`title-${language}`] || yoniDetails?.['title-en'] || moonYoni?.title || '';
  const yoniDescription =
    yoniDetails?.[`description-${language}`] || yoniDetails?.['description-en'] || '';
  // On prod CRA is built with PUBLIC_URL=/profile, so we have to prefix static
  // image paths — otherwise the browser asks for `/yoni/ashwa.jpg` which goes
  // through Yii routing instead of being served as a file.
  const yoniImage = yoniDetails?.image ? `${process.env.PUBLIC_URL || ''}${yoniDetails.image}` : null;

  const isCalculating = hasInputs && !error && !kundli;


  useEffect(() => {
    if (!moonYoni?.order) return;

    // Cancel any RAF still in flight (e.g. user switched avatars mid-animation).
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    // While the reveal is gated, hold the result hidden — circle, image and
    // copy are all driven by `progress`, so progress=0 keeps everything off.
    if (revealPending) {
      setProgress(0);
      hasAnimatedRef.current = false;
      skipAnimationRef.current = false;
      return;
    }

    if (!shouldAnimate || hasAnimatedRef.current) {
      setProgress(1);
      hasAnimatedRef.current = true;
      return;
    }

    skipAnimationRef.current = false;
    setProgress(0);
    let startTs;
    const step = (ts) => {
      if (skipAnimationRef.current) return;
      if (startTs === undefined) startTs = ts;
      const t = clamp01((ts - startTs) / ANIMATION_DURATION_MS);
      setProgress(t);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        hasAnimatedRef.current = true;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [moonYoni?.id, moonYoni?.order, shouldAnimate, revealPending]);


  const animationActive = Boolean(moonYoni) && !revealPending && shouldAnimate && progress < 1;
  const textOpacity = clamp01((progress - 0.7) / 0.3);

  const handleSkipAnimation = () => {
    if (!animationActive) return;
    skipAnimationRef.current = true;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    hasAnimatedRef.current = true;
    setProgress(1);
  };

  // While the reveal flow is active (button not yet pressed, or animation in
  // flight after the press), hide the surrounding chrome — AvatarPanel,
  // Social/PublicSocial and Jyotish — so the user is focused on the reveal.
  const surroundingsHidden = revealPending || (revealTriggered && progress < 1);

  // After the result settles (progress=1) we stagger:
  //   t=0       progress reaches 1 (animation done or instant)
  //   t=500ms   background image starts fading in (transition ~0.6s)
  //   t=1100ms  result copy starts fading in (after the image is visible)
  //   t=1600ms  copy fade-in (0.5s transition) has finished → reveal the HD block
  useEffect(() => {
    if (!moonYoni || progress < 1) {
      setBgImageShown(false);
      setCopyShown(false);
      setHdShown(false);
      return undefined;
    }
    const tImg = setTimeout(() => setBgImageShown(true), 500);
    const tCopy = setTimeout(() => setCopyShown(true), 1100);
    const tHd = setTimeout(() => setHdShown(true), 1600);
    return () => {
      clearTimeout(tImg);
      clearTimeout(tCopy);
      clearTimeout(tHd);
    };
  }, [moonYoni?.id, progress >= 1]);


  const handleReveal = () => {
    // Strip ?reveal=true from the address bar without leaving a history entry.
    const url = new URL(window.location.href);
    if (url.searchParams.has('reveal')) {
      url.searchParams.delete('reveal');
      window.history.replaceState(null, '', url.toString());
    }
    // Make sure the animation gets to play even though we're on a token URL.
    hasAnimatedRef.current = false;
    setRevealTriggered(true);
    setTimeout(() => {
      setRevealPending(false);
    }, 1000);
  };


  const goToYoniWithParams = (data) => {
    if (!data || !data.city_id || !data.dob) {
      console.error('Missing required data: city_id or dob');
      return;
    }
    const dobDate = new Date(data.dob);
    const year = dobDate.getFullYear();
    const month = String(dobDate.getMonth() + 1).padStart(2, '0');
    const day = String(dobDate.getDate()).padStart(2, '0');
    const hours = String(dobDate.getHours()).padStart(2, '0');
    const minutes = String(dobDate.getMinutes()).padStart(2, '0');
    const date = `${year}-${month}-${day}`;
    const time = `${hours}:${minutes}`;
    const cityName = data.city?.city || '';
    const timezone = data.city?.timezone || '';
    const url = `/yoni/?city=${encodeURIComponent(cityName)}&date=${date}&time=${time}&timezone=${timezone}&city_id=${data.city_id}&copy=true#n=simple`;
    window.location.href = url;
  };


  const navigate = useNavigate();

  // "Calculate now" CTA: a token URL (/yoni/<token>) leads to the full profile
  // (/u/<token>); a share-link URL (/yoni/?city=...&date=...) leads to the
  // public chart (/public/?...) keeping the original query string.
  const handleCalculateNow = () => {
    if (useravatarId) {
      navigate(`/u/${useravatarId}`);
    } else {
      navigate(`/public/${location.search}`);
    }
  };

  const handleAstroClick = (what = 'astro') => {
    let newPath = location.pathname.split('/study')[0];
    newPath = newPath.slice(-1) === '/' ? `${newPath}study/${what}` : `${newPath}/study/${what}`;
    const queryParams = location.search || '';
    navigate(`${newPath}${queryParams}`);
  };

  return (
    <YoniLayout
      viewedUser={userFromToken}
      viewedAvatar={avatarFromToken}
      viewedFollowings={followingsFromToken} 
      viewedFollowers={followersFromToken}
      surroundingsHidden={surroundingsHidden}
    >





      <>



        <div className="yoni_page _fv _fgap1">




          {!hasInputs && useravatarId && (
            // Token in the URL (e.g. /yoni/<token>?reveal=true) but avatar not
            // fetched yet — show the brand preloader instead of the landing CTA.
            <PreloaderHD nologo />
          )}



          {!hasInputs && !useravatarId && (
            <>



              {/* Slowly cross-fading yoni images drift across the very back. */}
              <YoniImageBackground />

              {/* Decorative slowly-rotating sanskrit wheel behind the
                    landing logo/CTA — the same YoniCircle frozen at progress=0. */}
              <YoniCircle
                progress={0}
                targetOrder={1}
                language={context.language}
                idle
              />


              <div className="yoni_page_content _fv _fgap1 _fc _fm">

                <YoniLogo calcShown={calcShown} />

                {calcShown && (
                  <div className="x0_search x0_search--unlogged yoni_calc_form">
                    <CityDateTime
                      publicUser={true}
                      avatar={{}}
                      showForm={calcShown}
                      updateHandler={goToYoniWithParams}
                      avatarDate={null}
                      avatarTime={null}
                      avatarOffset={null}
                      avatarCityId={null}
                      citySearchActive={false}
                      setShowForm={(val) => setCalcShown(val)}
                    />
                  </div>
                )}




                <>

                  {!calcShown && <div className="x_add_calltoaction">
                    <div
                      className="x_ad_cta x_ad_cta--1"
                      style={{ position: 'relative' }}
                      onClick={() => setCalcShown(true)}
                    >
                      {$ui ? $ui('jyotish_yoni_calculate_now') : 'Calculate now'}
                    </div>
                  </div>}

                  <div className="x_ad_languages0">
                    <a
                      className={`x_ad_lang ${context.language === 'en' ? 'active' : ''}`}
                      onClick={() => context.setLanguage && context.setLanguage('en')}
                    >
                      <div>English</div>
                    </a>
                    <a
                      className={`x_ad_lang ${context.language === 'ru' ? 'active' : ''}`}
                      onClick={() => context.setLanguage && context.setLanguage('ru')}
                    >
                      <div>По-русски</div>
                    </a>
                  </div>

                </>



              </div>
            </>
          )}



          {hasInputs && (
            <div className={`yoni_result0 ${hdShown && moonYoni && !revealPending && !isCalculating && !error ? 'yoni_result0--shown' : ''}`} ref={resultScrollRef}>

              {/* Slowly-rotating sanskrit wheel + cross-fading yoni images sit
                behind the loading state and the reveal screen, until the reveal
                animation takes over. */}
              {(isCalculating || revealPending) && (
                <>
                  <YoniImageBackground />
                  <YoniCircle
                    progress={0}
                    targetOrder={moonYoni?.order || 1}
                    language={language}
                    idle
                  />
                </>
              )}

              {isCalculating && (
                <div className="_tc _t12 yoni_result_status">
                  {$ui ? $ui('loading') : '…'}
                </div>
              )}

              {error && (
                <div className="_tc _t08 yoni_result_status yoni_result_status--error">
                  {error}
                </div>
              )}

              {kundli && !moonYoni && (
                <div className="_tc _t08 yoni_result_status">
                  {moonNakshatraName || '—'}
                </div>
              )}



              {moonYoni && revealPending && (
                <div className="yoni_reveal0 _fv _fc _fm _fgap1">

                  <YoniLogo calcShown={calcShown} />


                  <div className="yoni_dob _fv _fc _fm _fgap0">

                    {$ui ? $ui('jyotish_yoni_reveal_dob_label') : 'For DOB:'}

                    <strong>
                      {avatarFromToken ? (
                        <AvatarDob
                          user={userFromToken}
                          noClick={true}
                          showInfo={false}
                          avatar={avatarFromToken}
                        />
                      ) : (
                        avatarDobAvatar && <AvatarDob
                          user={userFromToken}
                          noClick={true}
                          showInfo={false}
                          avatar={avatarDobAvatar}
                        />
                      )}
                    </strong>

                    <a href="/yoni/" className="_tc yoni_wrong_date _o04">
                      {$ui('jyotish_yoni_reveal_wrong_date')}
                    </a>

                  </div>


                  <button
                    type="button"
                    className="x_ad_cta x_ad_cta--1"
                    onClick={handleReveal}
                  >
                    {$ui ? $ui('jyotish_yoni_reveal') : 'Reveal my Instinct Archetype'}
                  </button>


                </div>
              )}

              {moonYoni && !revealPending && (
                <>
                  {/* Yoni image fades in as the animation finishes; sits behind everything else. */}
                  {yoniImage && (
                    <img
                      ref={bgImageRef}
                      src={yoniImage}
                      alt=""
                      aria-hidden="true"
                      className={`yoni_bg_image ${bgImageShown ? 'yoni_bg_image--shown' : ''}`}
                    />
                  )}

                  {/* YoniCircle stays mounted: at progress=1 only the target Sanskrit
                    is left visible at viewport centre, so it never disappears or jumps. */}
                  <YoniCircle
                    progress={progress}
                    targetOrder={moonYoni.order}
                    language={language}
                  />

                  {animationActive && (
                    <button
                      type="button"
                      className="yoni_skip_btn"
                      onClick={handleSkipAnimation}
                    >
                      {$ui ? $ui('jyotish_yoni_skip') : 'Skip'}
                    </button>
                  )}

                  {/* Result copy fades in AFTER the background image, so the
                    visual hierarchy is animation → image → copy. */}
                  <div
                    className={`yoni_result_copy ${copyShown ? 'yoni_result_copy--shown' : ''}`}
                  >
                    <div className="_tc yoni_result_title">
                      {/* <span className="yoni_result_title_emoji">{moonYoni.emoji}</span>  */}
                      {yoniTitle}
                    </div>

                    {yoniDescription && (
                      <div className="_tc yoni_result_desc">
                        {yoniDescription}
                      </div>
                    )}

                        <br/>

                    <div className="_fv _fc _fm _fgap1">
                      <div className="_fv _fc _fm _f100 _fgap0 yoni_hd_mini">
                        <p className="_tc _t08 _o06">
                          {$ui('jyotish_title')}
                        </p>
                        <div className={`yoni_surroundings ${surroundingsHidden ? 'yoni_surroundings--hidden' : ''}`}>
                          <JyotishMini
                            avatar={avatarFromToken || avatarDobAvatar}
                            handleClick={() => { }}
                          />
                        </div>
                      </div>
                    </div>


                    <div className="_fv _fc _fm _f100 yoni_page_content_about">
                      <div className="_fv _fgap1 yoni_page_content_inner _t09">
                        <div className="yoni_page_description _tc">
                          <Txt>
                            {$ui('jyotish_description_long')}
                          </Txt>
                        </div>
                      </div>
                      <strong className="_tc _t09 _o06">{$ui('jyotish_beta')}</strong>
                    </div>

                    <div className="_fv _fc _fm _f100 yoni_hd0">
                      <div className="yoni_hd_cta">
                        <Button onClick={handleCalculateNow}>
                          {$ui('cta_calculate_now')}
                        </Button>
                      </div>
                      <div className="yoni_hd_preview">
                        <Preview
                          width={200}
                          height={200}
                          design={avatarFromToken?.design || avatarDobAvatar?.design} />
                      </div>
                    </div>

                  </div>



                </>
              )}


            </div>
          )}
        </div>
      </>

    </YoniLayout>
  );
}
